import { MeasurementUnit } from '@shure/shared/angular/data-access/system-api/models';

import { measurementUnitToRaw, pxToRaw, rawToMeasurementUnit, rawToPx } from './conversions';
import { unitBasedRound } from './round';

export class PositionUnit {
	// raw positional value in mm, devices supports precision down to 1/100 of a mm
	private readonly _rawValue: number;

	public get rawValue(): number {
		// round the raw value to 2 decimal point
		return Math.round(this._rawValue * 100) / 100;
	}

	private constructor(rawValue: number) {
		this._rawValue = rawValue;
	}

	public static fromRaw(rawValue: number): PositionUnit {
		return new PositionUnit(rawValue);
	}

	public static fromPixel(pixelValue: number): PositionUnit {
		return new PositionUnit(pxToRaw(pixelValue));
	}

	public static fromMeasurementUnit(value: number, measurementUnit: MeasurementUnit): PositionUnit {
		return new PositionUnit(measurementUnitToRaw(value, measurementUnit));
	}

	public static min(values: PositionUnit[]): PositionUnit {
		if (values.length === 0) {
			throw new Error();
		}

		return values.reduce((previous, current) => {
			return current._rawValue < previous._rawValue ? current : previous;
		});
	}

	public static max(values: PositionUnit[]): PositionUnit {
		if (values.length === 0) {
			throw new Error();
		}

		return values.reduce((previous, current) => {
			return current._rawValue > previous._rawValue ? current : previous;
		});
	}

	public add(coverageUnit: PositionUnit): PositionUnit {
		return new PositionUnit(this._rawValue + coverageUnit._rawValue);
	}

	public addRaw(rawValue: number): PositionUnit {
		return new PositionUnit(this._rawValue + rawValue);
	}

	public subtract(coverageUnit: PositionUnit): PositionUnit {
		return new PositionUnit(this._rawValue - coverageUnit._rawValue);
	}

	public divide(value: number): PositionUnit {
		return new PositionUnit(this._rawValue / value);
	}

	public multiply(value: number): PositionUnit {
		return new PositionUnit(this._rawValue * value);
	}

	public toMeasurementUnit(measurementUnit: MeasurementUnit): number {
		return unitBasedRound(rawToMeasurementUnit(this._rawValue, measurementUnit), measurementUnit);
	}

	public toPixelValue(): number {
		return rawToPx(this._rawValue);
	}
}
